import React, { useCallback, useState, memo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { XRaccoon, XUserInteraction } from '@core/utils';

// devExtreme
import { Button, NumberBox } from 'devextreme-react';
import { dxEvent } from 'devextreme/events';

// types
import {
  CountColumnItemProps,
  CountColumnItemActions,
  CountColumnItemInput
} from '../../types';

const CountColumnItem = ({
  dataGridRef,
  initValue,
  towarId,
  rowIndex,
  onCartUpdated,
  towarChangedData,
  countColumnItemsInputs
}: CountColumnItemProps) => {
  const [count, setCount] = useState<number>(initValue);
  const [enableInputHandler, setEnableInputHandler] = useState(false);
  const allowFocusNextRowHandlerCall = useRef(true);

  const focusNextRowInput = () => {
    if (!allowFocusNextRowHandlerCall.current) return;

    allowFocusNextRowHandlerCall.current = false;

    const dgInstance = dataGridRef.current?.instance;

    if (!dgInstance) return;

    const visibleRows = dgInstance.getVisibleRows();

    const towarIndex = visibleRows.findIndex((x) => x.key === towarId);
    const nextTowarId = visibleRows[towarIndex + 1]?.key;

    if (nextTowarId) {
      countColumnItemsInputs.current[nextTowarId]?.focus();
    }
  };

  const sendRequest = (value: number, focusNextRow: boolean = true) => {
    if (!dataGridRef.current) return;

    if (focusNextRow) {
      focusNextRowInput();
    }

    XRaccoon.Api.updateTowarKoszyk({
      Id: towarId,
      IloscTowarowWKoszyku: value
    })
      .then(() => {
        const dgInstance = dataGridRef.current?.instance;

        if (!dgInstance) return;

        towarChangedData.current[towarId] = value;

        dgInstance.repaintRows([rowIndex]);

        if (onCartUpdated) {
          onCartUpdated();
        }
      })
      .catch(XUserInteraction.notifyError);
  };

  const sendRequestDebounced = useCallback(
    debounce(
      (value, focusNextRow?: boolean) => sendRequest(value, focusNextRow),
      500
    ),
    [rowIndex]
  );

  const onBtnClick = (actionType: CountColumnItemActions) => () => {
    const valToAdd = actionType === 'ADD' ? 1 : -1;

    setCount((state) => {
      const newVal = state + valToAdd;

      if (newVal < 0) return state;

      sendRequestDebounced(newVal, false);

      return newVal;
    });
  };

  const onNumberBoxChanged = ({
    value,
    event
  }: {
    value?: number;
    event?: dxEvent | JQueryEventObject;
  }) => {
    const eventType = event ? (event as dxEvent & { type: string }).type : '';
    const newVal = value || 0;

    setCount(newVal);

    // debounce if changed by arrows
    if (eventType === 'change') {
      sendRequest(newVal);
    } else {
      sendRequestDebounced(newVal);
    }
  };

  const subscribeToRef = (component: CountColumnItemInput) => {
    countColumnItemsInputs.current[towarId] = component;
  };

  const unsubscribeFromRef = () => {
    countColumnItemsInputs.current[towarId] = undefined;
  };

  return (
    <div className="kategoria-towary-grid-count-column">
      <Button
        icon="minus"
        tabIndex={-1}
        stylingMode="text"
        onClick={onBtnClick('REMOVE')}
      />
      <NumberBox
        format="0#"
        min={0}
        onInitialized={({ component }) => subscribeToRef(component)}
        onDisposing={unsubscribeFromRef}
        onFocusIn={() => setEnableInputHandler(true)}
        onFocusOut={() => setEnableInputHandler(false)}
        value={count}
        onValueChanged={enableInputHandler ? onNumberBoxChanged : undefined}
      />
      <Button
        icon="plus"
        tabIndex={-1}
        stylingMode="text"
        onClick={onBtnClick('ADD')}
      />
    </div>
  );
};

export default memo(CountColumnItem);
